import React, { Fragment, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import compose from 'lodash.flowright';
import { graphql } from 'react-apollo';
import ReactTooltip from 'react-tooltip';
import { useHistory } from 'react-router';

import LogoutLink from '../../../../../components/auth/LogoutLink';
import withBasename from '../../../../../components/routing/withBasename';
import Angle from '../../../../../components/global/Angle';
import { IMAGE_SIZE_CODE } from '../../../../../constants/image-size-codes';
import { getProductBasenames } from '../../../../../util/role-definitions';
import { getThumbBySize } from '../../../../../util/photo';
import { getAsset } from '../../../../../util/assets';
import { MeQuery } from '../../../../../graphql/queries/account';
import DropdownMenu from '../../../../../components/global/DropdownMenu';
import FeatureBlock from '../../../../../components/contextual/FeatureBlock';
import {
  canAccessAccountBrandingOnBasename,
  canAccessAccountCreationsOnBasename,
  canAccessAccountMatchesOnBasename,
  canAccessAccountPricingOnBasename,
} from '../../../../../util/product-permissions';
import AssistanceActionButton from '../../../components/AssistanceActionButton';
import SignOutIcon from '../../../../../components/icons/SignOutIcon';
import BookmarkIcon from '../../../../../components/icons/BookmarkIcon';
import PriceTagIcon from '../../../../../components/icons/PriceTagIcon';
import { isEligibleForShutterbugFreeTrial } from '../../../../temp/views/onboarding/PhotoSearchCheckout/helpers';
import { isEligibleForPhotoManagerFreeTrial } from '../../Account/AccountPhotoManagerSettings/components/PhotoManagerSubscriptionCheckout/helpers';
import {
  BASENAME,
  VIDEO_TUTORIAL_URLS,
} from '../../../../../constants/basenames';
import MyProfileIcon from '../../../../../components/icons/MyProfileIcon';
import SettingsIcon from '../../../../../components/icons/SettingsIcon';
import DownloadIcon from '../../../../../components/icons/DownloadIcon';
import { isProAccount } from '../../../../../util/account';
import DraftWaldogramsIcon from '../../../../../components/icons/DraftWaldogramsIcon';
import MyMatchesIcon from '../../../../../components/icons/MyMatchesIcon';
import ShopIcon from '../../../../../components/icons/ShopIcon';
import SearchIcon from '../../../../../components/icons/SearchIcon';
import Button from '../../../../../components/global/Button';
import useActionableItemMenu from '../UserActionableItemMenu/useActionableItemMenu';
import NotificationCounter from '../../../components/NotificationCounter';
import StatusCounter from '../../../components/NotificationCounter/components/StatusCounter';
import AlbumFolderGalleryIcon from '../../../../../components/icons/AlbumFolderGalleryIcon';
import PlayButtonIcon from '../../../../../components/icons/PlayButtonIcon';
import useDismissiblePersistent from '../../../../../hooks/useDismissiblePersistent';
import { overrideTooltipPosition } from '../../../../../util/tooltips';
import EmailIcon from '../../../../../components/icons/EmailIcon';

const propTypes = {
  basename: PropTypes.oneOf(getProductBasenames()).isRequired,
  className: PropTypes.string,
  avatarPhoto: PropTypes.shape({
    thumbnail_urls: PropTypes.arrayOf(
      PropTypes.shape({
        size_code: PropTypes.string.isRequired,
        url: PropTypes.string.isRequired,
        width: PropTypes.number.isRequired,
        height: PropTypes.number.isRequired,
      }),
    ),
  }),
  account: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
};

const UserAccountMenu = ({ basename, className, avatarPhoto, account }) => {
  const history = useHistory();
  const {
    onNotificationsSidebarToggle,
    actionableItems,
  } = useActionableItemMenu();

  const smallThumbnail =
    getThumbBySize(avatarPhoto?.thumbnail_urls, IMAGE_SIZE_CODE.SMALL)?.url ??
    getAsset('/user-menu-default.png');

  const classes = classNames({
    'user-account-menu': true,
    'navbar-right-button': true,
    [className]: !!className,
  });

  const isConsumer = basename === BASENAME.SHUTTERBUG;

  const canRequestFreeTrial = isConsumer
    ? isEligibleForShutterbugFreeTrial(account)
    : isEligibleForPhotoManagerFreeTrial(account);

  const subscriptionsUrl = isConsumer ? 'subscriptions' : 'photo-manager';

  const actionableItemCount = actionableItems?.total ?? 0;
  const hasActionableItems = actionableItemCount > 0;

  const {
    visible: videoTutorialTooltipVisible,
    onDismiss: onVideoTutorialTooltipDismiss,
  } = useDismissiblePersistent(`${basename}_VIDEO_TUTORIALS_AVAILABLE`);

  useEffect(() => {
    ReactTooltip.rebuild();
    if (
      videoTutorialTooltipVisible &&
      [BASENAME.PRO, BASENAME.CAMPS, BASENAME.SCHOOLS].includes(basename)
    ) {
      ReactTooltip.show(
        document.querySelector(
          '[data-tip][data-for="video-tutorial-available"]',
        ),
      );
    } else {
      ReactTooltip.hide();
    }
  }, [videoTutorialTooltipVisible]);

  return (
    <div
      className={classes}
      data-tip
      data-for="video-tutorial-available"
      data-event="custom"
      data-event-off="customOff"
    >
      <ReactTooltip
        place="bottom"
        id="video-tutorial-available"
        className="tooltip video-tutorial-tooltip"
        globalEventOff="customOff"
        effect="solid"
        overridePosition={overrideTooltipPosition}
      >
        <span className="highlighted-text">NEW</span>
        Video tutorials available!
      </ReactTooltip>
      <DropdownMenu
        placement="bottom-end"
        onToggleSuccess={onVideoTutorialTooltipDismiss}
        toggle={
          <Fragment>
            <div
              id="user-account-menu-selfie-circle-btn"
              className="user-account-menu-selfie-circle-button"
              style={{ backgroundImage: `url("${smallThumbnail}")` }}
              onClick={onVideoTutorialTooltipDismiss}
              onKeyPress={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  onVideoTutorialTooltipDismiss(e);
                }
              }}
              role="button"
              tabIndex="0"
            >
              {hasActionableItems && (
                <StatusCounter
                  className="notification-counter-count"
                  count={actionableItemCount}
                />
              )}
            </div>
            <Angle direction="down" />
          </Fragment>
        }
        toggleClassName="user-account-menu-toggle-button"
        closeOnClickWithin
      >
        {hasActionableItems && (
          <Button
            btnStyle="reset"
            className="dropdown-item"
            onClick={onNotificationsSidebarToggle}
          >
            <NotificationCounter
              className="mr-2"
              size={20}
              count={actionableItemCount}
            />{' '}
            Notifications
          </Button>
        )}
        <FeatureBlock condition={canRequestFreeTrial}>
          <Button
            // TODO as per the designs this needs to say diff things
            // for different states i.e. the user is subscribed or not etc
            className="dropdown-item user-account-menu-action-item"
            onClick={() =>
              history.push(
                `/app/${basename}/account/settings/${subscriptionsUrl}`,
              )
            }
          >
            <img
              src={getAsset('/diamond-orange.svg')}
              className="upgrade-freemium-icon"
              alt="Upgrade subscription"
            />{' '}
            Upgrade subscription
          </Button>
          <div className="dropdown-item-separator" />
        </FeatureBlock>
        <FeatureBlock condition={canAccessAccountMatchesOnBasename(basename)}>
          <Button
            className="dropdown-item navbar-item-mobile-only"
            onClick={() => history.push(`/app/${basename}`)}
          >
            <AlbumFolderGalleryIcon
              size={20}
              className="mr-2"
              color="#50a6ff"
              id="account-dropdown-matches-icon"
            />{' '}
            My Events
          </Button>
          <Button
            className="dropdown-item navbar-item-mobile-only"
            onClick={() => history.push(`/app/${basename}/identities`)}
          >
            <MyMatchesIcon
              size={20}
              className="mr-2"
              color="#50a6ff"
              id="account-dropdown-matches-icon"
            />{' '}
            My people
          </Button>
          {account?.entitled_photos?.total > 0 && (
            <Button
              className="dropdown-item navbar-item-mobile-only"
              onClick={() => history.push(`/app/${basename}/shop`)}
            >
              <ShopIcon size={20} className="mr-2" color="#50a6ff" /> Store
            </Button>
          )}
          <Button
            className="dropdown-item"
            onClick={() => history.push(`/app/${basename}/search`)}
          >
            <SearchIcon size={20} className="mr-2" color="#50a6ff" /> Have a
            join code?
          </Button>
          <div className="dropdown-item-separator" />
        </FeatureBlock>
        <div className="dropdown-item dropdown-item-section-title">
          My Account
        </div>
        <Button
          className="dropdown-item"
          id="user-account-menu-settings-btn"
          onClick={() =>
            history.push(`/app/${basename}/account/settings/details`)
          }
        >
          <SettingsIcon color="#50a6ff" size={20} className="mr-2" /> Account
          Settings
        </Button>
        {account?.username && isProAccount(account) && (
          <Button
            className="dropdown-item"
            onClick={() =>
              history.push(`/app/${basename}/profile/@${account.username}`)
            }
          >
            <MyProfileIcon color="#50a6ff" size={20} className="mr-2" /> View
            Profile Page
          </Button>
        )}
        <FeatureBlock condition={canAccessAccountPricingOnBasename(basename)}>
          <Button
            className="dropdown-item"
            onClick={() =>
              history.push(`/app/${basename}/account/settings/pricing`)
            }
          >
            <PriceTagIcon color="#50a6ff" size={20} className="mr-2" /> My
            Products
          </Button>
        </FeatureBlock>
        <FeatureBlock condition={canAccessAccountBrandingOnBasename(basename)}>
          <Button
            className="dropdown-item"
            onClick={() =>
              history.push(`/app/${basename}/account/settings/branding`)
            }
          >
            <BookmarkIcon color="#50a6ff" size={20} className="mr-2" /> My
            Branding
          </Button>
        </FeatureBlock>
        <FeatureBlock condition={canAccessAccountCreationsOnBasename(basename)}>
          <Button
            className="dropdown-item"
            onClick={() => history.push(`/app/${basename}/account/cards/list`)}
          >
            <DraftWaldogramsIcon color="#50a6ff" size={20} className="mr-2" />{' '}
            My Cards &amp; Collages
          </Button>
        </FeatureBlock>
        {account?.photo_downloads?.total > 0 && (
          <Button
            className="dropdown-item"
            onClick={() =>
              history.push(`/app/${basename}/account/settings/downloads`)
            }
          >
            <DownloadIcon color="#50a6ff" size={20} className="mr-2" /> Recent
            Downloads
          </Button>
        )}
        <div className="dropdown-item-separator" />
        <div className="dropdown-item dropdown-item-section-title">Help </div>
        {VIDEO_TUTORIAL_URLS[basename] && (
          <div>
            <a
              href={VIDEO_TUTORIAL_URLS[basename]}
              target="_blank"
              className="dropdown-item"
              rel="noreferrer"
            >
              <PlayButtonIcon color="#50a6ff" size={20} className="mr-2" />
              Video tutorials
            </a>
          </div>
        )}{' '}
        <AssistanceActionButton
          className="dropdown-item"
          icon={<EmailIcon width={20} color="#0D84FE" className="mr-2" />}
        />
        <div className="dropdown-item-separator" />
        <LogoutLink
          className="dropdown-item"
          icon={<SignOutIcon color="#fe5851" size={20} className="mr-2" />}
        />
      </DropdownMenu>
    </div>
  );
};

UserAccountMenu.propTypes = propTypes;

const withPhoto = graphql(MeQuery, {
  props: ({ data }) => ({
    avatarPhoto: data?.me?.avatar_photo,
  }),
});

export { UserAccountMenu as UserAccountMenuComponent };

export default compose(withBasename, withPhoto)(UserAccountMenu);
